import { createSlice } from '@reduxjs/toolkit';
import { resolveJourneyApplicationNumbers } from '../common/utils/journeyEncryption';

const initialJourneyState = {
    currentStage: 'lead',
    applicationNumber: null,
    /** Encrypted value for URL query params (VITE_JOURNEY_QUERY_ENCRYPTION_KEY). */
    applicationNumberEncrypted: null,
    apiCache: {},
    journeyData: null,
    caseId: null,
    leadResult: null,
    quoteInitResult: null,
    quoteResult: null,
    premiumResult: null,
    premiumLoading: false,
    /** Ref-count for overlapping premium API calls; premiumLoading stays true while count > 0. */
    premiumLoadingCount: 0,
    premiumError: null,
    leadFormData: null,
    multiDimensionPremiumResult: null,
    /** Multi-dimension results keyed by plan name (Plus/Elite) when user is eligible for both */
    multiDimensionByPlan: null,
    /** Rider selection for quote: { selectedIds: string[], covers: { [riderId]: number } } - used for premium refetch */
    quoteRiders: { selectedIds: [], covers: {} },
    /** ROP/ECBO toggle state for premium calc (Scenarios 1–3). Default NO. */
    quoteOptions: { premiumRefundEnabled: false, enhancedCoverageEnabled: false },
    /** Quote cover selections (Life Cover) so the UI restores on new tabs. */
    quoteCoverAmount: null,
    quoteCustomCoverAmount: '',
    quoteCoverTillAge: null,
    quoteCustomCoverTillAge: '',
    /** Quote UI selections so refresh/new tabs restores exactly what user selected. */
    quotePremiumFrequency: 'Yearly',
    quotePaymentTermType: 'Regular Pay',
    quoteSelectedLimitedPayTerm: 5,
    customerVerificationData: null,
    paymentFlowMode: null,
    paymentTransactionStatus: null,
    formDefinition: null,
    stepData: {},
    proposalData: null,
  documents: {},
  policyStatusData: null,
  biApiProductId: null,
    declarations: {
    benefitDeclaration: false,
    termsAgreement: false,
    autoRenewal: true,    
  },
  /**
   * From product pincode master: which Super Suraksha variants are serviceable at the user's pincode.
   * null = not evaluated yet (treat as both allowed in Quote).
   */
  pincodeTermPlanAvailability: null,
};

const HYDRATE_ALLOWED_KEYS = new Set(Object.keys(initialJourneyState));

const journeySlice = createSlice({
  name: 'journey',
  initialState: initialJourneyState,
  reducers: {
    setStage(state, action) { state.currentStage = action.payload; },
    setApplicationNumber(state, action) {
      const { plain, encrypted } = resolveJourneyApplicationNumbers(action.payload);
      state.applicationNumber = plain;
      state.applicationNumberEncrypted = encrypted;
    },
    setCaseId(state, action) { state.caseId = action.payload; },
    setLeadResult(state, action) { state.leadResult = action.payload; },
    setLeadFormData(state, action) { state.leadFormData = {...(state.leadFormData || {}), ...(action.payload || {}), };},
    setQuoteInitResult(state, action) { state.quoteInitResult = action.payload; },
    setQuoteResult(state, action) { state.quoteResult = action.payload; },
    setPremiumResult(state, action) { state.premiumResult = action.payload; },
    setPremiumLoading(state, action) {
      state.premiumLoading = action.payload;
      if (!action.payload) {
        state.premiumLoadingCount = 0;
      } else {
        state.premiumLoadingCount = Math.max(state.premiumLoadingCount || 0, 1);
      }
    },
    beginPremiumLoading(state) {
      state.premiumLoadingCount = (state.premiumLoadingCount || 0) + 1;
      state.premiumLoading = state.premiumLoadingCount > 0;
    },
    endPremiumLoading(state) {
      state.premiumLoadingCount = Math.max(0, (state.premiumLoadingCount || 0) - 1);
      state.premiumLoading = state.premiumLoadingCount > 0;
    },
    setPremiumError(state, action) { state.premiumError = action.payload; },
    setMultiDimensionPremium(state, action) { state.multiDimensionPremiumResult = action.payload; },
    setMultiDimensionByPlan(state, action) { state.multiDimensionByPlan = action.payload; },
    setDeclarations: (state, action) => {
      state.declarations = {
        ...state.declarations,
        ...action.payload,
      };
    },
    setBiApiProductId(state, action) {
      state.biApiProductId = action.payload;
    },
    setPolicyStatusData(state, action) {
      state.policyStatusData = action.payload;
    },
    /** Full replace — callers pass the full documents map (needed for removals + refresh hydration). */
    setDocuments(state, action) {
      const p = action.payload;
      state.documents =
        p && typeof p === 'object' && !Array.isArray(p) ? { ...p } : {};
    },
    setQuoteRiders(state, action) {
      const { selectedIds = [], covers = {} } = action.payload || {};
      state.quoteRiders = { selectedIds: Array.isArray(selectedIds) ? selectedIds : [], covers: covers && typeof covers === 'object' ? covers : {} };
    },
    setQuoteOptions(state, action) {
      if (!state.quoteOptions) state.quoteOptions = { premiumRefundEnabled: false, enhancedCoverageEnabled: false };
      const { premiumRefundEnabled, enhancedCoverageEnabled } = action.payload || {};
      if (typeof premiumRefundEnabled === 'boolean') state.quoteOptions.premiumRefundEnabled = premiumRefundEnabled;
      if (typeof enhancedCoverageEnabled === 'boolean') state.quoteOptions.enhancedCoverageEnabled = enhancedCoverageEnabled;
    },
    setQuoteCoverAmount(state, action) {
      state.quoteCoverAmount = action.payload ?? null;
    },
    setQuoteCustomCoverAmount(state, action) {
      state.quoteCustomCoverAmount = action.payload ?? '';
    },
    setQuoteCoverTillAge(state, action) {
      state.quoteCoverTillAge = action.payload ?? null;
    },
    setQuoteCustomCoverTillAge(state, action) {
      state.quoteCustomCoverTillAge = action.payload ?? '';
    },
    /** Convenience: set both selected and custom cover amount values. */
    setQuoteCoverAmountSelection(state, action) {
      const { selectedCoverAmount, customCoverAmount } = action.payload || {};
      state.quoteCoverAmount = selectedCoverAmount ?? null;
      state.quoteCustomCoverAmount = customCoverAmount ?? '';
    },
    /** Convenience: set both selected and custom cover till-age values. */
    setQuoteCoverTillAgeSelection(state, action) {
      const { selectedCoverTillAge, customCoverTillAge } = action.payload || {};
      state.quoteCoverTillAge = selectedCoverTillAge ?? null;
      state.quoteCustomCoverTillAge = customCoverTillAge ?? '';
    },
    setQuotePremiumFrequency(state, action) {
      state.quotePremiumFrequency = action.payload ?? 'Yearly';
    },
    setQuotePaymentTermType(state, action) {
      state.quotePaymentTermType = action.payload ?? 'Regular Pay';
    },
    setQuoteSelectedLimitedPayTerm(state, action) {
      state.quoteSelectedLimitedPayTerm = action.payload ?? 5;
    },
    setApiCache(state, action) {
      const { key, data } = action.payload;
      state.apiCache[key] = {
        data,
        timestamp: Date.now()
      };
    },
    setJourneyData(state, action) {
      const journey = action.payload ?? null;
      if (!journey || typeof journey !== 'object') {
        state.journeyData = journey;
        return;
      }
      const rawApp =
        journey.applicationNumber ??
        journey.application_number ??
        state.applicationNumber;
      const { plain, encrypted } = resolveJourneyApplicationNumbers(rawApp);
      state.journeyData = {
        ...journey,
        ...(plain
          ? {
              applicationNumber: plain,
              application_number: plain,
              applicationNumberEncrypted: encrypted,
            }
          : {}),
      };
      if (plain) {
        state.applicationNumber = plain;
        state.applicationNumberEncrypted = encrypted;
      }
    },
    setPincodeTermPlanAvailability(state, action) {
      const p = action.payload;
      if (p == null) {
        state.pincodeTermPlanAvailability = null;
        return;
      }
      if (typeof p !== 'object' || Array.isArray(p)) {
        state.pincodeTermPlanAvailability = null;
        return;
      }
      state.pincodeTermPlanAvailability = {
        plusAllowed: p.plusAllowed !== false,
        eliteAllowed: p.eliteAllowed !== false,
      };
    },

    clearApiCache(state) {
      state.apiCache = {};
    },
    /** Merge multi-dimension result for a (plan, coverAmount, policyTerm) into the 3-level cache. */
    setMultiDimensionForPlanAndCover(state, action) {
      const { plan, coverAmount, policyTerm, result, contextKey } = action.payload;
      if (!state.multiDimensionByPlan) state.multiDimensionByPlan = {};
      if (!state.multiDimensionByPlan[plan]) state.multiDimensionByPlan[plan] = {};
      const coverKey = String(coverAmount);
      if (!state.multiDimensionByPlan[plan][coverKey]) state.multiDimensionByPlan[plan][coverKey] = {};
      if (policyTerm != null) {
        state.multiDimensionByPlan[plan][coverKey][String(policyTerm)] = result;
        if (contextKey) {
          state.multiDimensionByPlan[plan][coverKey][`ctx:${contextKey}::${String(policyTerm)}`] = result;
        }
      } else {
        state.multiDimensionByPlan[plan][coverKey]._default = result;
        if (contextKey) {
          state.multiDimensionByPlan[plan][coverKey][`ctx:${contextKey}::_default`] = result;
        }
      }
    },
    /** Batch-merge several (coverAmount → multi result) for one plan at a given policyTerm. */
    mergeMultiDimensionForPlanAndCovers(state, action) {
      const { plan, coverToResult, policyTerm, contextKey } = action.payload || {};
      if (!plan || !coverToResult || typeof coverToResult !== 'object') return;
      if (!state.multiDimensionByPlan) state.multiDimensionByPlan = {};
      if (!state.multiDimensionByPlan[plan]) state.multiDimensionByPlan[plan] = {};
      Object.entries(coverToResult).forEach(([coverAmount, result]) => {
        if (result == null) return;
        const coverKey = String(coverAmount);
        if (!state.multiDimensionByPlan[plan][coverKey]) state.multiDimensionByPlan[plan][coverKey] = {};
        if (policyTerm != null) {
          state.multiDimensionByPlan[plan][coverKey][String(policyTerm)] = result;
          if (contextKey) {
            state.multiDimensionByPlan[plan][coverKey][`ctx:${contextKey}::${String(policyTerm)}`] = result;
          }
        } else {
          state.multiDimensionByPlan[plan][coverKey]._default = result;
          if (contextKey) {
            state.multiDimensionByPlan[plan][coverKey][`ctx:${contextKey}::_default`] = result;
          }
        }
      });
    },
    /**
     * Populate the 3-level cache from a bulk premium API response.
     * Payload: { plan, results: { "SA_PT": multiDimensionResult } }
     * Keys are split on "_" to extract coverAmount and policyTerm.
     */
    setBulkPremiumResults(state, action) {
      const { plan, results, contextKey } = action.payload || {};
      if (!plan || !results || typeof results !== 'object') return;
      if (!state.multiDimensionByPlan) state.multiDimensionByPlan = {};
      if (!state.multiDimensionByPlan[plan]) state.multiDimensionByPlan[plan] = {};
      Object.entries(results).forEach(([key, multiResult]) => {
        if (multiResult == null) return;
        const sepIdx = key.indexOf('_');
        if (sepIdx < 0) return;
        const coverKey = key.substring(0, sepIdx);
        const ptKey = key.substring(sepIdx + 1);
        if (!state.multiDimensionByPlan[plan][coverKey]) {
          state.multiDimensionByPlan[plan][coverKey] = {};
        }
        state.multiDimensionByPlan[plan][coverKey][ptKey] = multiResult;
        if (contextKey) {
          state.multiDimensionByPlan[plan][coverKey][`ctx:${contextKey}::${ptKey}`] = multiResult;
        }
      });
    },
    setCustomerVerificationData(state, action) { state.customerVerificationData = {...state.customerVerificationData, ...action.payload}; },
    setPaymentFlowMode(state, action) { state.paymentFlowMode = action.payload; },
    setPaymentTransactionStatus(state, action) { state.paymentTransactionStatus = action.payload ?? null; },
    setFormDefinition(state, action) { state.formDefinition = action.payload; },
    saveStepData(state, action) {
      const { step, data } = action.payload;
      state.stepData[step] = data;
    },
    leadComplete(state, action) {
      state.currentStage = 'quote';
      state.leadResult = action.payload.leadResult;
      state.leadFormData = action.payload.formData;
      state.quoteOptions = { premiumRefundEnabled: false, enhancedCoverageEnabled: false };
      state.quotePremiumFrequency = 'Yearly';
      state.premiumError = null;
    },
    quoteComplete(state, action) {
      state.currentStage = 'summary';
      state.quoteResult = action.payload;
    },
    skipPayment(state) {
      state.currentStage = 'customerVerification';
      state.paymentFlowMode = 'PAY_LAST';
    },
    navigateTo(state, action) {
      state.currentStage = action.payload;
    },
    setProposalData(state, action) {
      state.proposalData = action.payload;
    },
    /**
     * Hydrate journey state from a serialized session snapshot.
     * Used on app bootstrap before user continues the flow.
     */
    hydrateJourneyState(state, action) {
      const payload = action.payload;
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
      Object.keys(payload).forEach((key) => {
        if (!HYDRATE_ALLOWED_KEYS.has(key)) return;
        if (key === 'applicationNumber') {
          const { plain, encrypted } = resolveJourneyApplicationNumbers(payload[key]);
          state.applicationNumber = plain;
          if (encrypted) {
            state.applicationNumberEncrypted = encrypted;
          }
          return;
        }
        if (key === 'applicationNumberEncrypted') {
          state.applicationNumberEncrypted = payload[key];
          return;
        }
        state[key] = payload[key];
      });
    },
    /** Reset journey state (e.g. after inactivity timeout or refresh); does not affect backend-saved data. */
    resetJourney() {
      return initialJourneyState;
    },
    /**
     * After transgender Stage 1.0 lead capture: drop client-side journey ids and lead draft
     * so the user can start fresh without applicationNumber in URL or storage.
     */
    clearTransientLeadCaptureState(state) {
      state.applicationNumber = null;
      state.applicationNumberEncrypted = null;
      state.caseId = null;
      state.leadFormData = null;
      state.stepData = {};
    },
  },
});

export const journeyActions = journeySlice.actions;
export default journeySlice.reducer;
